/**
 * Motion for the company home page.
 *
 * CSS carries the entrances (the headline, the stair cascading in, the card
 * wipes). This file runs everything that needs live numbers:
 *
 *   the light field   WebGL light that follows the cursor (src/fx/lightfield.js)
 *   the staircase     tilts with the cursor; each glass step catches the light
 *                     where the light actually is on screen
 *   the book          opens as it scrolls into view, then a page turns
 *   magnetic buttons  pulled toward a nearby cursor
 *   the flash         fired in the shader the moment "YOU!" lands
 *
 * It only ever writes custom properties and classes, so if it never runs the
 * page is simply still and fully readable.
 *
 * Tiers — the same ladder the handbook page's 3D book uses (core/capability.js):
 *   reduced motion         nothing runs; CSS switches every animation off too
 *   STATIC / Save-Data     no WebGL: the CSS light burst stays; book and stair run
 *   LOW / MID / HIGH       WebGL at rising resolution and noise detail, with the
 *                          frame-rate governor stepping down if a device struggles
 *   coarse pointer         no cursor: the light drifts on its own instead
 *   ?tier=0..3             force a tier for testing
 */

import { detectTier, tierOverride, TIER, QualityGovernor } from './core/capability.js';
import { LightField } from './fx/lightfield.js';

// Shared with the Meet Ava & Artie page (src/meet-effects.js), which reuses
// the light, the rim light and the magnets below.
export const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
export const finePointer = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false;
const conn = navigator.connection;
export const lite = Boolean(conn && conn.saveData) || (navigator.deviceMemory !== undefined && navigator.deviceMemory <= 2);

export const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
export const lerp = (a, b, t) => a + (b - a) * t;
export const smooth = (t) => t * t * (3 - 2 * t);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

/** Render scale (fraction of CSS px) and noise octaves per tier. */
const GL_QUALITY = {
  [TIER.LOW]: { scale: 0.5, octaves: 3 },
  [TIER.MID]: { scale: 0.62, octaves: 4 },
  [TIER.HIGH]: { scale: 0.78, octaves: 5 },
};

export function initHomeEffects() {
  if (lite) document.documentElement.classList.add('fx-lite');
  if (reduceMotion) return;

  const hero = document.querySelector('.home .hero');
  if (!hero) return;

  const light = startLight(hero);
  staircase(hero, light);
  if (light) rimLight(hero, light);
  if (finePointer) tileSpotlights(hero);
  bookOpens();
  if (finePointer) magnets();

  // "YOU!" lands ~1.05s after first paint (see the char-punch timing in
  // home.css). Fire the flash then, measured from first paint rather than from
  // whenever this module happened to load.
  if (light) {
    const fp = performance.getEntriesByType?.('paint').find((e) => e.name === 'first-paint');
    const delay = Math.max(0, (fp ? fp.startTime : performance.now()) + 1050 - performance.now());
    setTimeout(() => light.flash(), delay);
  }
}

/* --------------------------------------------------------------------------- */
/*  The light field                                                             */
/* --------------------------------------------------------------------------- */

/**
 * @param {HTMLElement} hero  the section holding .fx / .lightfield
 * @param {object} [o]
 * @param {string|null} [o.anchor]  element the light rests behind, if shown
 * @param {[number,number]} [o.anchorAt]  where on that element (0..1, y down)
 * @param {[number,number]} [o.rest]  resting place without it (0..1, y up)
 * @param {[number,number]} [o.xClamp]  how far the cursor may pull it, in x
 * The defaults are the home page's own.
 */
export function startLight(hero, {
  anchor = '.duo .ava',
  anchorAt = [0.55, 0.36],
  rest = [0.85, 0.22],
  xClamp = [0.38, 0.97],
} = {}) {
  const canvas = hero.querySelector('.lightfield');
  if (!canvas || lite) return null;

  const tier = tierOverride() ?? detectTier().tier;
  if (tier === TIER.STATIC) return null;

  // Hide the CSS burst before the first frame so the two never double up.
  hero.classList.add('gl-on');
  let field = LightField.create(canvas, { ...GL_QUALITY[tier], onLost: () => fallBack() });
  if (!field) {
    hero.classList.remove('gl-on');
    return null;
  }

  // Positions are 0..1 with y UP, matching the shader. With Ava and Artie on
  // screen, the light rests BEHIND Ava at chest height, so she stands in it and
  // her rim catches it. Without them (under 1200px) it rests where the burst
  // sits on the book cover, just past the top of the staircase. Measured from
  // the real layout rather than hard-coded, and re-measured on resize.
  const ava = anchor ? hero.querySelector(anchor) : null;
  const base = [...rest];
  const placeBase = () => {
    const r = hero.getBoundingClientRect();
    const a = ava?.getBoundingClientRect();
    if (a && a.width > 2 && r.width > 0) {
      base[0] = (a.left + a.width * anchorAt[0] - r.left) / r.width;
      base[1] = 1 - (a.top + a.height * anchorAt[1] - r.top) / r.height;
    } else {
      base[0] = rest[0];
      base[1] = rest[1];
    }
  };
  placeBase();
  const target = [...base];
  const pos = [...base];
  let cursor = false;
  let flash = 0;
  let running = false;
  let visible = true;
  let raf = 0;
  let last = 0;
  const t0 = performance.now();
  const listeners = [];
  let rect = hero.getBoundingClientRect();

  const governor = new QualityGovernor({
    tier,
    onChange: (next) => (next <= TIER.STATIC ? fallBack() : field.setQuality(GL_QUALITY[next])),
  });

  function fallBack() {
    stop();
    governor.dispose();
    field?.dispose();
    field = null;
    hero.classList.remove('gl-on'); // the CSS burst returns, with its own entrance
  }

  if (finePointer) {
    hero.addEventListener('pointermove', (e) => {
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1 - (e.clientY - rect.top) / rect.height;
      // Follow the cursor strongly but keep the source on the open side of the
      // hero, away from the headline and the stair.
      target[0] = clamp(lerp(base[0], x, 0.8), xClamp[0], xClamp[1]);
      target[1] = clamp(lerp(base[1], y, 0.8), 0.06, 0.94);
      cursor = true;
    });
    hero.addEventListener('pointerleave', () => { cursor = false; });
  }

  const measure = () => { rect = hero.getBoundingClientRect(); };
  window.addEventListener('scroll', measure, { passive: true });
  window.addEventListener('resize', () => { measure(); placeBase(); field?.resize(); });
  // Images arrive after first layout; re-place the light once they have a size.
  ava?.addEventListener('load', () => { measure(); placeBase(); });
  window.addEventListener('load', () => { measure(); placeBase(); });

  function frame(now) {
    raf = 0;
    if (!running || !field) return;
    const dt = Math.min(0.05, (now - (last || now)) / 1000);
    last = now;
    const time = (now - t0) / 1000;

    if (!cursor) {
      // no cursor (phones, or the pointer is elsewhere): drift on its own
      target[0] = base[0] + 0.055 * Math.sin(time * 0.21);
      target[1] = base[1] + 0.05 * Math.cos(time * 0.27);
    }
    const k = 1 - Math.exp(-dt * 3.2);
    pos[0] += (target[0] - pos[0]) * k;
    pos[1] += (target[1] - pos[1]) * k;
    flash *= Math.exp(-dt * 2.2);

    const scroll = clamp(-rect.top / Math.max(1, rect.height), 0, 1);
    const ly = pos[1] - 0.22 * scroll; // the light sinks as the hero scrolls away
    field.render({
      time,
      light: [pos[0], ly],
      ignite: easeOut(clamp((time - 0.05) / 1.9, 0, 1)),
      flash,
      scroll,
    });
    governor.tick(now);

    // where the light is on screen, for anything that should catch it
    const sx = rect.left + pos[0] * rect.width;
    const sy = rect.top + (1 - ly) * rect.height;
    for (const fn of listeners) fn(sx, sy);

    raf = requestAnimationFrame(frame);
  }

  function start() {
    if (running || !field) return;
    running = true;
    last = 0;
    governor.reset();
    raf = requestAnimationFrame(frame);
  }

  function stop() {
    running = false;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
  }

  // Only draw while the hero is on screen and the tab is visible.
  new IntersectionObserver(([e]) => {
    visible = e.isIntersecting;
    visible && !document.hidden ? start() : stop();
  }).observe(hero);
  document.addEventListener('visibilitychange', () => (visible && !document.hidden ? start() : stop()));
  start();

  return {
    flash: () => { flash = 1; },
    onMove: (fn) => listeners.push(fn),
  };
}

/* --------------------------------------------------------------------------- */
/*  The staircase                                                              */
/* --------------------------------------------------------------------------- */

function staircase(hero, light) {
  const stair = hero.querySelector('.tiles');
  const steps = [...hero.querySelectorAll('.tile')];
  if (!stair) return;

  if (finePointer) {
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
      const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
      stair.style.setProperty('--ty', `${(-13 + nx * 11).toFixed(2)}deg`);
      stair.style.setProperty('--tx', `${(8 - ny * 8).toFixed(2)}deg`);
    });
    hero.addEventListener('pointerleave', () => {
      stair.style.removeProperty('--tx');
      stair.style.removeProperty('--ty');
    });
  }

  // Each glass step catches the light where the light really is. Read every
  // rect first, then write, so the loop never forces a layout per step.
  if (light) {
    light.onMove((sx, sy) => {
      const rects = steps.map((s) => s.getBoundingClientRect());
      steps.forEach((s, i) => {
        s.style.setProperty('--sx', `${Math.round(sx - rects[i].left)}px`);
        s.style.setProperty('--sy', `${Math.round(sy - rects[i].top)}px`);
      });
    });
  }
}

/**
 * Ava and Artie catch the light: a warm glow on the edge that faces the WebGL
 * light, brighter the closer it is. drop-shadow re-rasterises the image when it
 * changes, so values are only written when they move enough to see — the light
 * eases slowly, so most frames write nothing.
 */
export function rimLight(hero, light, selector = '.duo .ava, .duo .artie') {
  const figures = [...hero.querySelectorAll(selector)];
  if (!figures.length) return;
  const lastWritten = new WeakMap();

  light.onMove((sx, sy) => {
    const rects = figures.map((f) => f.getBoundingClientRect());
    figures.forEach((f, i) => {
      const r = rects[i];
      if (r.width < 2) return; // not displayed on this screen size
      const dx = sx - (r.left + r.width / 2);
      const dy = sy - (r.top + r.height * 0.4);
      const dist = Math.hypot(dx, dy) || 1;
      const reach = 7;
      const x = (dx / dist) * Math.min(reach, dist * 0.03);
      const y = (dy / dist) * Math.min(reach, dist * 0.03);
      // was 0.8; brought down with the field itself (review, 2026-09-10: too bright)
      const a = clamp(1.25 - dist / 520, 0, 1) * 0.45;
      const prev = lastWritten.get(f);
      if (prev && Math.abs(prev[0] - x) < 0.4 && Math.abs(prev[1] - y) < 0.4 && Math.abs(prev[2] - a) < 0.03) return;
      lastWritten.set(f, [x, y, a]);
      f.style.setProperty('--rx', `${x.toFixed(1)}px`);
      f.style.setProperty('--ry', `${y.toFixed(1)}px`);
      f.style.setProperty('--ra', a.toFixed(2));
    });
  });
}

/** Each step also lights up in its own accent colour under the cursor. */
function tileSpotlights(hero) {
  for (const tile of hero.querySelectorAll('.tile')) {
    tile.addEventListener('pointermove', (e) => {
      const r = tile.getBoundingClientRect();
      tile.style.setProperty('--mx', `${Math.round(e.clientX - r.left)}px`);
      tile.style.setProperty('--my', `${Math.round(e.clientY - r.top)}px`);
    });
  }
}

/* --------------------------------------------------------------------------- */
/*  The book                                                                   */
/* --------------------------------------------------------------------------- */

/**
 * Opens as it scrolls into view: the cover swings open on its spine over the
 * first 60% of the travel, then page 3 turns over. It starts turned away and
 * ends facing the reader, tipped back a little like a book on a desk.
 */
function bookOpens() {
  const wrap = document.querySelector('.home .cover-wrap');
  const book = wrap?.querySelector('.book');
  if (!wrap || !book) return;

  let onScreen = false;
  let queued = false;
  let hx = 0;
  let hy = 0;

  const update = () => {
    queued = false;
    const r = wrap.getBoundingClientRect();
    const vh = window.innerHeight;
    // 0 as the book's top enters the bottom of the screen; 1 once it is
    // comfortably up into the view
    const t = clamp((vh - r.top) / (vh * 0.7 + r.height * 0.4), 0, 1);
    const c = smooth(clamp(t / 0.6, 0, 1));
    const l = smooth(clamp((t - 0.6) / 0.4, 0, 1));
    book.style.setProperty('--c', c.toFixed(4));
    book.style.setProperty('--l', l.toFixed(4));
    book.style.setProperty('--o', c.toFixed(4));
    book.style.setProperty('--ry', `${(lerp(-24, 0, c) + hx * 8).toFixed(2)}deg`);
    book.style.setProperty('--rx', `${(lerp(14, 10, t) - hy * 5).toFixed(2)}deg`);
  };

  const request = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };

  new IntersectionObserver(([e]) => {
    onScreen = e.isIntersecting;
    if (onScreen) request();
  }).observe(wrap);
  window.addEventListener('scroll', () => onScreen && request(), { passive: true });
  window.addEventListener('resize', request);

  if (finePointer) {
    wrap.addEventListener('pointermove', (e) => {
      const r = wrap.getBoundingClientRect();
      hx = clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1, 1);
      hy = clamp(((e.clientY - r.top) / r.height) * 2 - 1, -1, 1);
      request();
    });
    wrap.addEventListener('pointerleave', () => { hx = 0; hy = 0; request(); });
  }

  update(); // set the closed, turned-away pose while it's still below the fold
}

/* --------------------------------------------------------------------------- */
/*  Magnetic buttons                                                           */
/* --------------------------------------------------------------------------- */

export function magnets() {
  const els = [...document.querySelectorAll('.home .magnet')];
  if (!els.length) return;
  const offset = new WeakMap(); // current pull, so we measure the RESTING centre
  let mx = -1e4;
  let my = -1e4;
  let raf = 0;

  const apply = () => {
    raf = 0;
    const rects = els.map((el) => el.getBoundingClientRect());
    els.forEach((el, i) => {
      const [ox, oy] = offset.get(el) || [0, 0];
      const r = rects[i];
      const cx = r.left + r.width / 2 - ox;
      const cy = r.top + r.height / 2 - oy;
      const dx = mx - cx;
      const dy = my - cy;
      const reach = Math.max(r.width, r.height) * 0.75 + 44;
      const pull = Math.max(0, 1 - Math.hypot(dx, dy) / reach);
      const x = dx * 0.32 * pull;
      const y = dy * 0.42 * pull;
      offset.set(el, [x, y]);
      el.style.setProperty('--mgx', `${x.toFixed(1)}px`);
      el.style.setProperty('--mgy', `${y.toFixed(1)}px`);
    });
  };

  window.addEventListener('pointermove', (e) => {
    mx = e.clientX;
    my = e.clientY;
    if (!raf) raf = requestAnimationFrame(apply);
  }, { passive: true });
}
